import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import "dayjs/locale/ar";
import {
  DashboardNarrativePanel,
  type SortDirection,
} from "src/components/common";
import { useDashboardNarrative } from "src/hooks/UseDashboardNarrative";
import { useDashboardPdfRowOverride } from "src/hooks/UseDashboardPdfTableExpansion";
import { getApiLangFromI18n } from "src/utils/apiLang";
import {
  formatNumber,
  formatNumberDigits,
} from "src/utils/numberFormat";
import {
  fetchCLTVCustomerAnalytics,
  type CustomerAnalyticsRow,
} from "src/services/CLTVDashboardAPI";
import {
  CLTV_SEGMENT_COLORS,
  defaultCltvSegmentColor,
} from "./components/CLTVDashboard.data";
import {
  cltvSegmentNameSlug,
  getCltvColumnDefinition,
  getCltvSectionDefinition,
} from "./components/CltvDefinitions";
import { useCLTVDashboard } from "./hooks/UseCLTVDashboard";
import CltvDashboardHeader from "./components/CltvDashboardHeader";
import CltvKpiCards from "./components/CltvKpiCards";
import CltvCustomerSegmentDistribution from "./components/CltvCustomerSegmentDistribution";
import CltvPredictiveInsights from "./components/CltvPredictiveInsights";
import CltvActualVsPredictedRevenue from "./components/CltvActualVsPredictedRevenue";
import CltvSegmentEvolution from "./components/CltvSegmentEvolution";
import CltvCustomerAnalyticsTable from "./components/CltvCustomerAnalyticsTable";
import {
  buildSegmentToneResolver,
  CLTV_PDF_TABLE_MAX_ROWS,
  CLTV_PDF_TABLE_PAGE_SIZE,
  escapeCsvCell,
  extractCurrencyCode,
  formatMoneyCell,
  getCustomerLifetimeLabel,
  getCustomerSegmentLabel,
  getCustomerSegmentTone,
  getDefaultMonthRange,
  getPredictedCltv,
  getPredictedOrders,
  getPredictedSpend,
  getTotalOrders,
  getTotalSpend,
  isAverageMonthlySpendKpiCard,
  mapPredictiveInsight,
  SEGMENT_EVOLUTION_SERIES,
  SORT_KEY_TO_API_FIELD,
  toLocaleDigits,
  type CustomerTableSortKey,
} from "./components/CltvDashboardShared";
import "./Styles/CltvDashboard.scss";

const CLTVDashboard: FC = () => {
  const { t, i18n } = useTranslation();
  const apiLang = getApiLangFromI18n(i18n);
  const isAr = (i18n.resolvedLanguage ?? i18n.language ?? "en").toLowerCase().startsWith("ar");
  const moneyLocale = isAr ? "ar-SA" : undefined;
  const formatMoney = useCallback(
    (value: number) =>
      `PKR ${value.toLocaleString(moneyLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    [moneyLocale],
  );
  const formatMoneyShort = useCallback(
    (value: number) => {
      const compact = (scaled: number, suffix: string) => {
        const fractionDigits = scaled % 1 === 0 ? 0 : 1;
        return `PKR ${scaled.toLocaleString(moneyLocale, {
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits,
        })}${suffix}`;
      };

      const abs = Math.abs(value);
      if (abs >= 1_000_000) {
        return compact(value / 1_000_000, "M");
      }
      if (abs >= 1_000) {
        return compact(value / 1_000, "K");
      }
      return `PKR ${value.toLocaleString(moneyLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    [moneyLocale],
  );
  const formatKLabel = useCallback(
    (value: number) => {
      if (value === 0) {
        return (0).toLocaleString(moneyLocale);
      }
      if (Math.abs(value) >= 1000000) {
        const millions = value / 1000000;
        const fractionDigits = millions % 1 === 0 ? 0 : 1;
        return `${millions.toLocaleString(moneyLocale, {
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits,
        })}M`;
      }
      return `${Math.round(value / 1000).toLocaleString(moneyLocale)}K`;
    },
    [moneyLocale],
  );
  const formatSpendingBucket = useCallback(
    (raw: string) => {
      const slug = cltvSegmentNameSlug(raw.replace(/[()[\]]/g, " "));
      const tk = `cltv.spendingBuckets.${slug}`;
      const translated = t(tk, { defaultValue: "" });
      if (translated && translated !== tk) {
        return translated;
      }
      if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
        const d = new Date(`${raw.slice(0, 10)}T12:00:00`);
        if (!Number.isNaN(d.getTime())) {
          return d.toLocaleDateString(isAr ? "ar-SA" : "en-US", {
            month: "short",
            year: "numeric",
          });
        }
      }
      return raw;
    },
    [t, isAr],
  );
  const formatMonthYear = useCallback(
    (value: string) => (isAr ? dayjs(value).locale("ar") : dayjs(value)).format("MMM YYYY"),
    [isAr],
  );
  const formatCount = useCallback(
    (value: number | null | undefined) =>
      value === null || value === undefined ? "" : formatNumber(value, {}, i18n.language),
    [i18n.language],
  );
  // Unisolated so Arabic renders the `%` on the left of the digits, matching the KPI trend
  // percentages and the Sales & Products cards.
  const formatPercent = useCallback(
    (value: number | null | undefined) =>
      value === null || value === undefined
        ? ""
        : `${formatNumberDigits(value, { maximumFractionDigits: 1 }, i18n.language)}%`,
    [i18n.language],
  );
  const emptyCell = t("cltv.table.notAvailable");
  const customerTableTooltip = useMemo(() => getCltvSectionDefinition("customerTable", t), [t]);
  const columnTooltips = useMemo(
    () => ({
      profile: getCltvColumnDefinition("profile", t),
      totalSpend: getCltvColumnDefinition("totalSpend", t),
      totalOrders: getCltvColumnDefinition("totalOrders", t),
      predictedSpend: getCltvColumnDefinition("predictedSpend", t),
      predictedOrders: getCltvColumnDefinition("predictedOrders", t),
      segments: getCltvColumnDefinition("segments", t),
    }),
    [t],
  );
  const resolveSegmentTone = useMemo(() => buildSegmentToneResolver(t), [t]);
  const segmentTonesForRow = useCallback(
    (row: CustomerAnalyticsRow) => getCustomerSegmentTone(row, resolveSegmentTone),
    [resolveSegmentTone],
  );
  const defaultRange = getDefaultMonthRange();
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [isPdfCapturing, setIsPdfCapturing] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedSegments, setSelectedSegments] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [tableSort, setTableSort] = useState<{
    key: CustomerTableSortKey;
    direction: SortDirection;
  } | null>(null);
  const pageSize = 10;
  // Actual vs Predicted Revenue always shows a fixed 6-month window and isn't
  // affected by the date-range filter, so once it has loaded once we keep it
  // visible during background refetches instead of re-showing the spinner —
  // that flicker made it look like the date filter was changing this chart.
  const [hasLoadedSpendingPatterns, setHasLoadedSpendingPatterns] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, debouncedSearch, selectedSegments, tableSort]);

  const apiSortBy = tableSort ? SORT_KEY_TO_API_FIELD[tableSort.key] : undefined;
  const apiSortDir = tableSort ? tableSort.direction : undefined;
  const apiSegment = selectedSegments.length > 0 ? selectedSegments : undefined;

  const {
    kpiCards,
    segmentDistribution,
    predictiveInsights,
    spendingPatterns,
    segmentEvolution,
    customerAnalytics,
    kpiLoading,
    kpiError,
    chartsError,
    customerLoading,
    customerError,
  } = useCLTVDashboard({
    startDate,
    endDate,
    lang: apiLang,
    page,
    pageSize,
    search: debouncedSearch,
    segment: apiSegment,
    sortBy: apiSortBy,
    sortDir: apiSortDir,
  });

  useEffect(() => {
    if (spendingPatterns.length > 0) {
      setHasLoadedSpendingPatterns(true);
    }
  }, [spendingPatterns]);

  const narrativeCurrency = useMemo(
    () =>
      extractCurrencyCode(kpiCards[0]?.formattedValue) ??
      extractCurrencyCode(spendingPatterns[0]?.predictedFormattedValue ?? spendingPatterns[0]?.actualFormattedValue) ??
      undefined,
    [kpiCards, spendingPatterns],
  );

  const narrativePayloadData = useMemo<Record<string, unknown>>(
    () => ({
      kpis: {
        kpiCards,
        segmentDistribution,
        predictiveInsights,
      },
      spending_patterns: {
        ...(narrativeCurrency ? { currency: narrativeCurrency } : {}),
        currentMonth: endDate,
        data: spendingPatterns,
      },
      segment_evolution: {
        data: segmentEvolution,
      },
    }),
    [kpiCards, segmentDistribution, predictiveInsights, spendingPatterns, segmentEvolution, endDate, narrativeCurrency],
  );

  const { narrative: cltvNarrative, loading: narrativeLoading, aiSummary } = useDashboardNarrative({
    dashboardKey: "cltv_dashboard",
    tabKey: "overview",
    startDate,
    endDate,
    data: narrativePayloadData,
    enabled: !kpiLoading && !kpiError && Boolean(kpiCards.length || segmentDistribution.length),
  });

  const visibleKpiCards = useMemo(
    () => kpiCards.filter(card => !isAverageMonthlySpendKpiCard(card)),
    [kpiCards],
  );

  const pieData = useMemo(() => {
    return [...segmentDistribution]
      .filter(segment => (segment.count ?? 0) > 0)
      .sort((a, b) => (b.count ?? 0) - (a.count ?? 0) || a.name.localeCompare(b.name))
      .map(s => {
        // `name` is localized by the API, so only `nameKey` (its canonical English name) can key
        // the palette and the `cltv.segmentNames.*` copy — keeping the Arabic bars colored and
        // labelled exactly like the English ones.
        const segmentKey = s.nameKey ?? s.name;

        return {
          segmentKey,
          name: t(`cltv.segmentNames.${cltvSegmentNameSlug(segmentKey)}`, { defaultValue: s.name }),
          value: s.count,
          countFormattedValue: s.countFormattedValue,
          percent: s.percent,
          color: CLTV_SEGMENT_COLORS[segmentKey] ?? defaultCltvSegmentColor,
        };
      });
  }, [segmentDistribution, t]);

  const insightRows = useMemo(() => predictiveInsights.map(mapPredictiveInsight), [predictiveInsights]);

  const pagination = customerAnalytics?.pagination;
  const customerRows = useMemo(() => customerAnalytics?.data ?? [], [customerAnalytics]);

  // Sorting and segment filtering are handled server-side; customerRows is already the correct page.
  const sortedCustomerRows = customerRows;

  const {
    rows: exportCustomerRows,
    onBeforeCapture: onBeforeCustomerTableCapture,
    onAfterCapture: onAfterCustomerTableCapture,
  } = useDashboardPdfRowOverride(sortedCustomerRows, async () => {
    const loadedRows: CustomerAnalyticsRow[] = [];
    let exportPage = 1;
    let exportTotalPages = 1;
    while (loadedRows.length < CLTV_PDF_TABLE_MAX_ROWS && exportPage <= exportTotalPages) {
      const response = await fetchCLTVCustomerAnalytics({
        primary_date: endDate,
        lang: apiLang,
        page: exportPage,
        page_size: CLTV_PDF_TABLE_PAGE_SIZE,
        search: debouncedSearch.trim() || undefined,
        segment: apiSegment,
        sort_by: apiSortBy,
        sort_order: apiSortDir,
      });
      loadedRows.push(...(response.data ?? []));
      exportTotalPages = Math.max(1, response.pagination?.totalPages ?? 1);
      if ((response.data ?? []).length === 0) {
        break;
      }
      exportPage += 1;
    }
    return loadedRows.slice(0, CLTV_PDF_TABLE_MAX_ROWS);
  });

  const totalRecords = pagination?.totalRecords ?? 0;

  const customerTableTotalPages = useMemo(() => {
    if (pagination && typeof pagination.totalPages === "number" && pagination.totalPages > 0) {
      return pagination.totalPages;
    }
    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }, [pagination, totalRecords, pageSize]);

  const rangeSummary = useMemo(() => {
    // Dates keep the numeric DD-MM-YYYY form in both locales (only the digits are localized),
    // so the two months stay directly comparable at a glance.
    const from = toLocaleDigits(dayjs(startDate).format("DD-MM-YYYY"), isAr);
    const to = toLocaleDigits(dayjs(endDate).format("DD-MM-YYYY"), isAr);
    return `${from} ${t("cltv.common.vs")} ${to}`;
  }, [startDate, endDate, isAr, t]);

  const maxObservedSpend = useMemo(() => {
    if (!customerRows.length) {
      return 1;
    }

    const biggest = Math.max(...customerRows.map(row => getTotalSpend(row)));

    return biggest > 0 ? biggest : 1;
  }, [customerRows]);

  const spendingChartData = useMemo(() => {
    // Anchored on the real current date, not the date-range filter — this panel
    // always shows a fixed 6-month window and must not shift when the user
    // changes the primary/secondary month filter.
    const currentMonth = isAr ? dayjs().locale("ar") : dayjs();
    const timeline = Array.from({ length: 6 }, (_, index) => currentMonth.subtract(3 - index, "month"));
    const spendingByMonth = new Map(
      spendingPatterns.map(item => {
        const rawMonth = item.month ?? item.bucket ?? "";
        return [dayjs(rawMonth).format("YYYY-MM"), item];
      }),
    );
    const historicalGaps = timeline
      .map((monthPoint, index) => {
        if (index >= 3) {
          return null;
        }
        const item = spendingByMonth.get(monthPoint.format("YYYY-MM"));
        if (!item) {
          return null;
        }
        const predicted = Number(item.predicted) || 0;
        const actual = Number(item.actual) || 0;
        return actual > 0 ? Math.abs(actual - predicted) : null;
      })
      .filter((value): value is number => value !== null);
    const averageHistoricalGap = historicalGaps.length
      ? historicalGaps.reduce((sum, value) => sum + value, 0) / historicalGaps.length
      : 0;

    return timeline.map((monthPoint, index) => {
      const key = monthPoint.format("YYYY-MM");
      const item = spendingByMonth.get(key);
      const predicted = Number(item?.predicted) || 0;
      const actualRaw = Number(item?.actual) || 0;
      const isHistorical = index < 3;
      const isCurrent = index === 3;
      const actual = isHistorical && actualRaw > 0 ? actualRaw : null;
      const comparisonValues =
        actual !== null ? [predicted, actual] : [predicted];
      const pointMin = Math.min(...comparisonValues);
      const pointMax = Math.max(...comparisonValues);
      const spread = pointMax - pointMin;
      const preferredGap = averageHistoricalGap > 0 ? averageHistoricalGap * 0.35 : pointMax * 0.045;
      const padding = Math.max(preferredGap, spread * 0.18, 18000);

      return {
        label: monthPoint.format("MMM"),
        fullLabel: monthPoint.format("MMM YYYY"),
        predicted,
        actual,
        predictedRaw: item ? predicted : null,
        actualRaw: item ? actualRaw : null,
        predictedFormattedValue: item?.predictedFormattedValue,
        actualFormattedValue: actual !== null ? item?.actualFormattedValue : undefined,
        bandLower: Math.max(0, pointMin - padding),
        bandUpper: pointMax + padding,
        status: isHistorical ? "historical" : isCurrent ? "current" : "forecast",
      };
    });
  }, [isAr, spendingPatterns]);

  const spendingYAxisDomain = useMemo(() => {
    const values = spendingChartData.flatMap(point => [
      point.predicted,
      point.actual ?? null,
    ]).filter((value): value is number => value !== null && Number.isFinite(value));

    if (!values.length) {
      return [0, 100] as [number, number];
    }

    const max = Math.max(...values);
    const upperBound = max * 1.15;

    return [0, upperBound] as [number, number];
  }, [spendingChartData]);

  const spendingSubtitle = useMemo(() => {
    const currentMonth = (isAr ? dayjs().locale("ar") : dayjs()).format("MMMM YYYY");
    return t("cltv.charts.sixMonthView", { month: toLocaleDigits(currentMonth, isAr) });
  }, [isAr, t]);

  const formatCountAxis = useCallback(
    (value: number) => {
      if (value === 0) {
        return (0).toLocaleString(moneyLocale);
      }
      if (Math.abs(value) >= 1000) {
        return `${Math.round(value / 1000).toLocaleString(moneyLocale)}K`;
      }
      return value.toLocaleString(moneyLocale);
    },
    [moneyLocale],
  );

  const activeEvolutionSeries = useMemo(() => {
    const visible = SEGMENT_EVOLUTION_SERIES
      .map(series => ({
        ...series,
        latestValue: segmentEvolution[segmentEvolution.length - 1]?.[series.key] ?? 0,
        maxValue: Math.max(...segmentEvolution.map(point => point[series.key] ?? 0)),
      }))
      .filter(series => series.maxValue > 0)
      .sort((a, b) => b.latestValue - a.latestValue);

    return visible;
  }, [segmentEvolution]);

  const [isExporting, setIsExporting] = useState(false);

  const handleExportCustomerCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      // The backend allows page_size up to 1000 for this endpoint. Page through
      // the results in chunks and accumulate the rows to keep memory bounded
      // for very large datasets while still cutting down the number of requests.
      const EXPORT_PAGE_SIZE = 1000;
      const allRows: CustomerAnalyticsRow[] = [];
      let page = 1;
      let totalPages = 1;
      do {
        const res = await fetchCLTVCustomerAnalytics({
          primary_date: endDate,
          lang: apiLang,
          page,
          page_size: EXPORT_PAGE_SIZE,
          search: debouncedSearch.trim() || undefined,
          segment: apiSegment,
          sort_by: apiSortBy,
          sort_order: apiSortDir,
        });
        allRows.push(...res.data);
        totalPages = res.pagination.totalPages;
        page += 1;
      } while (page <= totalPages);

      const headers = [
        t("cltv.headers.customerName"),
        t("cltv.headers.customerEmail"),
        t("cltv.headers.customerProfile"),
        t("cltv.headers.totalSpend"),
        t("cltv.headers.totalOrders"),
        t("cltv.headers.predictedSpend3m"),
        t("cltv.headers.predictedOrders3m"),
        t("cltv.headers.predictedCltv"),
        t("cltv.headers.segments"),
      ];
      const rows = allRows.map(row => {
        const totalOrders = getTotalOrders(row);
        const totalSpend = getTotalSpend(row);
        const predictedOrders = getPredictedOrders(row);
        const predictedSpend = getPredictedSpend(row);
        const predictedCltv = getPredictedCltv(row);
        const monthsActive = Math.max(1, row.customerAgeMonths ?? totalOrders * 6);

        return [
          row.customerName ?? row.customerId,
          row.email || emptyCell,
          getCustomerLifetimeLabel(monthsActive, t, i18n.language),
          formatMoneyCell(row.totalSpendFormattedValue, totalSpend, formatMoney) ?? emptyCell,
          totalOrders,
          formatMoneyCell(row.predictedSpend3mFormattedValue, predictedSpend, formatMoney) ?? emptyCell,
          predictedOrders,
          formatMoneyCell(row.predictedLtv90dFormattedValue, predictedCltv, formatMoney) ?? emptyCell,
          segmentTonesForRow(row)
            .map(tone => getCustomerSegmentLabel(tone, t))
            .join("; ") || emptyCell,
        ];
      });
      const csv = [headers, ...rows]
        .map(row => row.map(escapeCsvCell).join(","))
        .join("\r\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `cltv-customer-analytics-${endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      // Never let an export failure surface as an uncaught runtime error.
      console.error("CLTV customer export failed", err);
      window.alert(t("cltv.errors.loadCustomerFailed"));
    } finally {
      setIsExporting(false);
    }
  }, [
    endDate,
    apiLang,
    debouncedSearch,
    apiSegment,
    apiSortBy,
    apiSortDir,
    formatMoney,
    segmentTonesForRow,
    emptyCell,
    i18n.language,
    t,
  ]);

  return (
    <div
      className={`cltv-dashboard-layout${isPdfCapturing ? " cltv-dashboard--pdf-capture" : ""}`}
      ref={pdfContainerRef}
    >
      <CltvDashboardHeader
        rangeSummary={rangeSummary}
        startDate={startDate}
        endDate={endDate}
        onRangeChange={({ start, end }) => {
          setStartDate(start);
          setEndDate(end);
        }}
        pdfContainerRef={pdfContainerRef}
        exportDisabled={kpiLoading || Boolean(kpiError)}
        onBeforeCapture={async () => {
          setIsPdfCapturing(true);
          await onBeforeCustomerTableCapture();
        }}
        onAfterCapture={() => {
          onAfterCustomerTableCapture();
          setIsPdfCapturing(false);
        }}
      />
      <div className="cltv-dashboard cltv-dashboard__main">

        {kpiError ? (
          <p className="cltv-dashboard__error-banner" role="alert">
            {kpiError}
          </p>
        ) : null}

        <div data-pdf-block="narrative">
          <DashboardNarrativePanel
            narrative={cltvNarrative}
            loading={kpiLoading || narrativeLoading}
            aiSummary={aiSummary}
          />
        </div>

        <CltvKpiCards loading={kpiLoading} cards={visibleKpiCards} />

        <section className="cltv-dashboard__dual" data-pdf-row="dual-panels">
          <CltvCustomerSegmentDistribution
            loading={kpiLoading}
            pieData={pieData}
            formatCount={formatCount}
          />
          <CltvPredictiveInsights
            loading={kpiLoading}
            predictiveInsights={predictiveInsights}
            insightRows={insightRows}
            formatCount={formatCount}
            formatPercent={formatPercent}
          />
        </section>

        <CltvActualVsPredictedRevenue
          loading={kpiLoading}
          hasLoadedSpendingPatterns={hasLoadedSpendingPatterns}
          chartsError={chartsError}
          spendingPatternsLength={spendingPatterns.length}
          spendingChartData={spendingChartData}
          spendingYAxisDomain={spendingYAxisDomain}
          spendingSubtitle={spendingSubtitle}
          formatKLabel={formatKLabel}
          formatSpendingBucket={formatSpendingBucket}
          formatMoney={formatMoney}
        />

        <CltvSegmentEvolution
          loading={kpiLoading}
          chartsError={chartsError}
          segmentEvolution={segmentEvolution}
          activeEvolutionSeries={activeEvolutionSeries}
          formatMonthYear={formatMonthYear}
          formatCountAxis={formatCountAxis}
        />

        <CltvCustomerAnalyticsTable
          isAr={isAr}
          customerTableTooltip={customerTableTooltip}
          columnTooltips={columnTooltips}
          searchInput={searchInput}
          onSearchInputChange={setSearchInput}
          selectedSegments={selectedSegments}
          onSelectedSegmentsChange={setSelectedSegments}
          setTableSort={setTableSort}
          customerError={customerError}
          customerLoading={customerLoading}
          exportCustomerRows={exportCustomerRows}
          sortedCustomerRowsLength={sortedCustomerRows.length}
          isExporting={isExporting}
          onExportCustomerCsv={handleExportCustomerCsv}
          emptyCell={emptyCell}
          formatMoneyShort={formatMoneyShort}
          formatCount={formatCount}
          maxObservedSpend={maxObservedSpend}
          segmentTonesForRow={segmentTonesForRow}
          page={page}
          setPage={setPage}
          customerTableTotalPages={customerTableTotalPages}
        />
      </div>
    </div>
  );
};

export default CLTVDashboard;
